import {ReactNode, ReactElement} from 'react';
import {
  useCountryCode,
  useGlobalizationPair,
} from 'mkt-ssr/lib/globalization/context';
import type {CountryCode, LocaleBCP47} from 'mkt-ssr/lib/globalization/types';

export interface LocaleGateProps {
  /**
   * The optional countries in which the component should be rendered or not
   */
  countries?: CountryCode | Array<CountryCode>;
  /**
   * The optional languages in which the component should be rendered or not
   */
  languages?: Array<LocaleBCP47> | LocaleBCP47;
  /**
   * If true, the component will be hidden in the specified countries/languages
   * If false or undefined, the component will be shown in the specified countries/languages
   */
  exclude?: boolean;
  /**
   * The component to be rendered with the specified rules
   */
  children: ReactNode;
  /**
   * The component to be rendered if the restriction rules do not pass
   */
  fallback?: ReactNode;
}

/**
 * Restricts rendering of its children based on the current user's country and/or language.
 *
 * @param countries - The country or list of countries in which the component should be rendered or not.
 * @param languages - The language or list of languages in which the component should be rendered or not.
 * @param exclude - If true, excludes the component in the specified countries/languages; if false or undefined, shows the component only in the specified countries/languages.
 * @param children - The React node(s) to render if the restriction rules pass.
 *
 * @returns The children if the current country and language match the specified rules, otherwise null.
 */
export function LocaleGate({
  countries,
  languages,
  exclude = false,
  children,
  fallback,
}: LocaleGateProps): ReactElement | null {
  const country = useCountryCode();
  let countriesToCheck: CountryCode[] | undefined;
  if (countries === undefined) {
    countriesToCheck = undefined;
  } else {
    countriesToCheck = Array.isArray(countries) ? countries : [countries];
  }

  const currentGlobalization = useGlobalizationPair();
  let languagesToCheck: LocaleBCP47[] | undefined;
  if (languages === undefined) {
    languagesToCheck = undefined;
  } else {
    languagesToCheck = Array.isArray(languages) ? languages : [languages];
  }

  const countryMatch = countriesToCheck
    ? countriesToCheck.includes(country)
    : true;

  const languageMatch = languagesToCheck
    ? languagesToCheck.includes(currentGlobalization.locale.bcp47)
    : true;

  const eitherMatches = countryMatch && languageMatch;

  if ((exclude && eitherMatches) || (!exclude && !eitherMatches)) {
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}
